import { type ReactNode, useMemo, useState } from 'react'
import styled from 'styled-components'

import {
  Button,
  CrossFadeImage,
  Tab,
  TabGroup,
  TypeStyle,
  ViewportSizes,
  fromMd,
  timingDefaultMs,
  useIsViewport,
  zIndexBackground,
  zIndexForeground,
  zIndexModal,
  zIndexPopovers,
} from '@syconium/little-miss-figgy'

import {
  NextDialog,
  NextDialogDismiss,
  NextDialogProvider,
  NextDialogTrigger,
  useNextDialogContext,
} from '../../../../app/_components/dialogs/NextDialog.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { useExternalEmbroidery } from '../../../../containers/EmbroideryContainer/providers/facade'

// Static background images for learn more drawer
const TEXT_BG_DESKTOP =
  'https://images.ctfassets.net/5j6wpslh72e4/LE5UgVoRHehuSNS2PRUwD/943eed8010d3da71af38d847712f19d7/260116_TEXT_PDPEmbroideryMessaging_SiteAsset_Desktop.jpg'
const TEXT_BG_MOBILE =
  'https://images.ctfassets.net/5j6wpslh72e4/5FVzUoiXevUf22kpNaa6RN/7874cb75370f2426c14dcb12b42ebb6e/260116_TEXT_PDPEmbroideryMessaging_SiteAsset_Mobile.jpg'
const ICON_BG_DESKTOP =
  'https://images.ctfassets.net/5j6wpslh72e4/2mh9hLUFPR4MzHcM5vYzYy/81a26a772893a6a4feda6145a98cfb85/260116_ICON_PDPEmbroideryMessaging_SiteAsset_Desktop.jpg'
const ICON_BG_MOBILE =
  'https://images.ctfassets.net/5j6wpslh72e4/7qz8kIyHR0nPYKtg4zdnSo/2337fa41f2f04ac8a3c85264760aa5e7/260116_ICON_PDPEmbroideryMessaging_SiteAsset_Mobile.jpg'
const LOGO_BG_DESKTOP =
  'https://images.ctfassets.net/5j6wpslh72e4/5WXaGoCwU1XkF7IjTpVX9L/32a831b6f20415a666a95eb5fffce1dd/260116_LOGO_PDPEmbroideryMessaging_SiteAsset_Desktop.jpg'
const LOGO_BG_MOBILE =
  'https://images.ctfassets.net/5j6wpslh72e4/4LrYUfog9MbSm4Wqj7GwM2/322fe4c36e7101f2d8a49059f7c46914/260116_LOGO_PDPEmbroideryMessaging_SiteAsset_Mobile.jpg'

export const EmbroideryLearnMoreModal = () => {
  const { embroidery } = useTranslation()

  return (
    <NextDialogProvider>
      <NextDialogTrigger
        render={triggerProps => {
          return (
            <LearnMoreTextButton {...triggerProps}>
              {embroidery.learnMore.learnMoreCTA}
            </LearnMoreTextButton>
          )
        }}
      />
      <EmbroideryLearnMoreModalContent embroidery={embroidery} />
    </NextDialogProvider>
  )
}

type EmbroideryLearnMoreModalContentProps = {
  embroidery: ReturnType<typeof useTranslation>['embroidery']
}

enum TabType {
  TEXT,
  ICON,
  LOGO,
}

const EmbroideryLearnMoreModalContent = ({ embroidery }: EmbroideryLearnMoreModalContentProps) => {
  const dialogContext = useNextDialogContext()
  const { openEmbroideryModal } = useExternalEmbroidery()
  const [selectedTab, setSelectedTab] = useState<TabType>(TabType.LOGO)
  const isSmallViewport = useIsViewport(ViewportSizes.small)

  const backgroundImage = useMemo(() => {
    switch (selectedTab) {
      case TabType.ICON:
        return isSmallViewport ? ICON_BG_MOBILE : ICON_BG_DESKTOP
      case TabType.TEXT:
        return isSmallViewport ? TEXT_BG_MOBILE : TEXT_BG_DESKTOP
      case TabType.LOGO:
      default:
        return isSmallViewport ? LOGO_BG_MOBILE : LOGO_BG_DESKTOP
    }
  }, [selectedTab, isSmallViewport])

  const handleGetStartedClick = () => {
    dialogContext?.hide()
    openEmbroideryModal()
  }

  return (
    <StyledDrawer
      analyticsTitle='Embroidery Learn More'
      variant='right-drawer'
      content={<DrawerContent $backgroundImage={backgroundImage} closeText={embroidery.close} />}
    >
      <DrawerBody>
        <TabsContainer>
          <DrawerHeader>{embroidery.learnMore.tabGroupTitle}</DrawerHeader>
          <LearnMoreTabGroup
            onTabSelect={tabName => {
              switch (tabName) {
                case 'Icon':
                  setSelectedTab(TabType.ICON)
                  break
                case 'Text':
                  setSelectedTab(TabType.TEXT)
                  break
                case 'Logo':
                default:
                  setSelectedTab(TabType.LOGO)
                  break
              }
            }}
          >
            <Tab name='Logo' render={embroidery.learnMore.logoEmbroideryTitle}>
              <TabPanelText as='p'>{embroidery.learnMore.logoEmbroideryText}</TabPanelText>
            </Tab>
            <Tab name='Text' render={embroidery.learnMore.textEmbroideryTitle}>
              <TabPanelText as='p'>{embroidery.learnMore.textEmbroideryText}</TabPanelText>
            </Tab>
            <Tab name='Icon' render={embroidery.learnMore.iconEmbroideryTitle}>
              <TabPanelText as='p'>{embroidery.learnMore.iconEmbroideryText}</TabPanelText>
            </Tab>
          </LearnMoreTabGroup>
          <GetStartedButton
            aria-label={embroidery.learnMore.getStartedButton}
            onClick={handleGetStartedClick}
            aria-disabled={false}
          >
            {embroidery.learnMore.getStartedButton}
          </GetStartedButton>
        </TabsContainer>
      </DrawerBody>
    </StyledDrawer>
  )
}

const StyledDrawer = styled(NextDialog)`
  width: 100vw;
  max-width: 100vw;
  min-width: 100vw;

  ${fromMd} {
    max-width: min(600px, 100vw);
    min-width: auto;
  }
`

const DrawerContent = ({
  $backgroundImage,
  closeText,
  children,
}: {
  $backgroundImage: string
  closeText: string
  children?: ReactNode
}) => {
  return (
    <DrawerContentWrapper>
      <CloseButton aria-label={closeText} title={closeText} />
      <DrawerBackgroundImageFrame>
        <CrossFadeImage
          alt='' // decorative background — no useful info for screen readers but needed for underlying ResponsiveImage
          aspectRatios={{ sm: null, md: null }}
          src={$backgroundImage}
          transparentBackground
          widths={{ unit: 'vw', sm: 100, md: 100 }}
        />
      </DrawerBackgroundImageFrame>
      <DrawerForeground>{children}</DrawerForeground>
    </DrawerContentWrapper>
  )
}

const DrawerContentWrapper = styled.div`
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0;
`

const DrawerHeader = styled(TypeStyle.HeadlineExtraSmall)`
  font-size: 18px;
`

const DrawerBackgroundImageFrame = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: ${zIndexBackground};
`

const CloseButton = styled(NextDialogDismiss)`
  position: absolute;
  top: ${o => o.theme.spacing(8)};
  right: ${o => o.theme.spacing(6)};
  height: ${o => o.theme.spacing(8)};
  width: ${o => o.theme.spacing(8)};
  min-width: ${o => o.theme.spacing(8)};
  min-height: ${o => o.theme.spacing(8)};
  z-index: ${zIndexPopovers};

  ${fromMd} {
    top: ${o => o.theme.spacing(12)};
    right: ${o => o.theme.spacing(12)};
  }
`

const DrawerForeground = styled.div`
  position: relative;
  z-index: ${zIndexModal};
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`

const DrawerBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: ${o => o.theme.spacing(6, 4)};
  ${fromMd} {
    padding: ${o => o.theme.spacing(10)};
  }
`

const TabsContainer = styled.div`
  border-radius: 6px;
  min-height: 200px;
  border: 1px solid black;
  padding: ${o => o.theme.spacing(4)};
  margin-top: auto;
  background-color: ${o => o.theme.color.fill.background};
`

const LearnMoreTabGroup = styled(TabGroup)`
  padding: 0;

  /* hides the border on the parent div of the tab group */
  > div:first-child {
    border-bottom: none;
    background-color: ${o => o.theme.color.fill.subtle};
    border-radius: ${o => o.theme.spacing(20)};
    margin: ${o => o.theme.spacing(4, 0, 0, 0)};
    padding: ${o => o.theme.spacing(0.5, 1.25, 0.5, 0.5)};
    overflow-y: hidden;
  }

  /* Translate highlight to first position when first tab is selected */
  > div:first-child:has([role='tab']:nth-child(1)[aria-selected='true']) [role='tablist'] + div {
    transform: translateX(0%);
  }

  /* Translate highlight to second position when first tab is selected */
  > div:first-child:has([role='tab']:nth-child(2)[aria-selected='true']) [role='tablist'] + div {
    transform: translateX(100%);
  }

  /* Translate highlight to third position when third tab is selected */
  > div:first-child:has([role='tab']:nth-child(3)[aria-selected='true']) [role='tablist'] + div {
    transform: translateX(200%);
  }

  /* styles for the tablist container */
  [role='tablist'] {
    gap: 0;
    padding: 0;
    z-index: ${zIndexForeground};
    min-height: ${o => o.theme.spacing(7)};
    width: 100%;
  }

  /* use the indicator as a sliding pill */
  [role='tablist'] + div {
    display: block;
    background-color: ${o => o.theme.color.fill.background};
    border-radius: 80px;
    top: ${o => o.theme.spacing(0.5)};
    bottom: ${o => o.theme.spacing(0.5)};
    height: auto;
    z-index: ${zIndexModal};
    width: calc((100% - ${o => o.theme.spacing(1.25)}) / 3);
    left: ${o => o.theme.spacing(0.5)};
    transition: transform ${timingDefaultMs} ease;
  }

  /* styles for the selected tab*/
  [role='tab'][aria-selected='true'] {
    background-color: transparent;
    border-radius: 80px;
    color: ${o => o.theme.color.text.primary.on.background};
  }

  /* style for all tabs*/
  [role='tab'] {
    -webkit-tap-highlight-color: transparent;
    align-items: center;
    background: transparent;
    box-sizing: border-box;
    display: flex;
    flex: 1;
    text-align: center;
    justify-content: center;
    min-width: 0;
    color: ${o => o.theme.color.stroke.disabled.text.on.background};
    font-size: 11px;
    letter-spacing: 1.6px;
    min-height: ${o => o.theme.spacing(7)};
    padding: ${o => o.theme.spacing(1.75, 2.75)}; // 7px 11px
    position: relative;
    z-index: ${zIndexPopovers};
  }

  /* style for content of selected tab */
  [role='tabpanel'] {
    margin: ${o => o.theme.spacing(4, 0)};
    line-height: 22px;
  }
`

const TabPanelText = styled(TypeStyle.BodyTiny)`
  margin: 0;
  font-size: 13px;
`

const GetStartedButton = styled(Button)`
  width: 100%;
  height: ${o => o.theme.spacing(12.5)};
`

const LearnMoreTextButton = styled.button`
  ${TypeStyle.css.headlineMicro};
  text-transform: capitalize;
  letter-spacing: 0.55px;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 17px;
  color: ${o => o.theme.color.text.secondary.on.background};
  margin-inline-start: auto;
  text-decoration: underline;
  &:hover {
    color: ${o => o.theme.color.text.primary.on.background};
  }
`
